import { ActionIcon, Box, Group, Text, Tooltip } from "@mantine/core"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useTranslation } from "react-i18next"
import type { FlatItem } from "../models/documentCategory"

interface Props {
  item: FlatItem
  selectedId: number | null
  onSelect: () => void
  onDoubleClick: () => void
  onAddChild: () => void
  onToggleExpand: () => void
  isExpanded: boolean
}

export default function TreeItem({
  item,
  selectedId,
  onSelect,
  onDoubleClick,
  onAddChild,
  onToggleExpand,
  isExpanded,
}: Props) {
  const { t } = useTranslation()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    data: { type: "category" },
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }

  return (
    <Box ref={setNodeRef} style={style}>
      <Group
        gap={4}
        py={4}
        pr="md"
        pl={item.depth * 24 + 12}
        wrap="nowrap"
        bg={selectedId === item.id ? "blue.0" : undefined}
        style={{ borderRadius: 4 }}
        onDoubleClick={onDoubleClick}
      >
        <Tooltip label={t("common.dragToReorder")}>
          <ActionIcon
            variant="subtle"
            size="sm"
            color="gray"
            style={{ cursor: "grab" }}
            {...attributes}
            {...listeners}
          >
            <i className="fa fa-bars" />
          </ActionIcon>
        </Tooltip>

        {item.hasChildren ? (
          <ActionIcon variant="subtle" size="sm" onClick={onToggleExpand}>
            <i className={`fa fa-chevron-${isExpanded ? "down" : "right"}`} />
          </ActionIcon>
        ) : (
          <Box w={28} />
        )}

        <Text
          size="sm"
          style={{ cursor: "pointer", flex: 1 }}
          onClick={onSelect}
        >
          {item.hierarchy_name || item.name}
        </Text>

        <Tooltip label={t("common.edit")}>
          <ActionIcon variant="subtle" size="sm" onClick={onDoubleClick}>
            <i className="fa fa-pencil" />
          </ActionIcon>
        </Tooltip>

        <Tooltip label={t("documentCategories.addChild")}>
          <ActionIcon variant="subtle" size="sm" onClick={onAddChild}>
            <i className="fa fa-plus" />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Box>
  )
}
